import {ref, computed, onMounted} from 'vue';
import {defineStore} from "pinia";
import * as api from '../api'
import {
    ContextCompany,
    IUserTariffShortInfo,
    UserInfo,
    UserProfile
} from "@/entities/User/model/types";
import {setCookie, deleteCookie} from "@/shared/lib/utils/cookie";
import {isProd} from "@/shared/lib/utils/environment";
import {useRoute} from "vue-router";
import {abbreviations} from "@/entities/User/config";
import {useQuery} from "@tanstack/vue-query";
import {AxiosError} from "axios";
import {OrderApi} from '@/entities/Order';
import {getMobileOperatingSystem, TPlatform} from "@/shared/lib/utils/platform";
import {setUserData} from "@/shared/api/rocketChat";
import {useI18n} from "vue-i18n";

export const useUserStore = defineStore('userStore', () => {
    const userInfo = ref<UserInfo | null>(null);
    const isAuth = ref<boolean>(false);
    const loadingUserInfo = ref<boolean>(false);
    const userProfile = ref<UserProfile | null>(null);
    const isSelectCompanyBlocked = ref<boolean>(false);
    const isLoader = ref<boolean>(false);
    const userTariffShortInfo = ref<IUserTariffShortInfo>({
        active: false,
        type: 'loading',
    })
    const selectedCompanyBin = ref<string | null>(null);

    const contextCompanies = ref<ContextCompany[]>([]);

    const route = useRoute();

    const platform = ref<TPlatform | null>(null);
    const {locale} = useI18n();

    const selectedContextCompany = computed<ContextCompany | null>(() => {
        for (const company of contextCompanies.value) {
            if (selectedCompanyBin.value === company.bin) {
                return company;
            }
            if (!selectedCompanyBin.value && company.isMainCompany) {
                return company;
            }
        }
        return null;
    })

    const formatCompanyName = () => {
        contextCompanies.value.forEach(company => {
            if (!company.shortNameRu) {
                for (const abbreviation of abbreviations) {
                    if (abbreviation.pattern.test(company.nameRu)) {
                        company.shortNameRu = company.nameRu.replace(abbreviation.pattern, abbreviation.substring);
                    }
                }
            }
        })
    }

    const getUserInfo = async (uin?: string) => {
        loadingUserInfo.value = true;
        const bin = uin || localStorage.getItem('selectedCompanyBin');
        contextCompanies.value = await api.getCompanyFromContext();
        let found = false;
        for (const company of contextCompanies.value) {
            if (company.bin === bin) {
                found = true;
                break;
            }
        }
        if (!contextCompanies.value.length) {
            selectedCompanyBin.value = null;
        } else {
            if (!found) {
                await setMainCompany(contextCompanies.value[0]?.bin);
            } else {
                selectedCompanyBin.value = bin;
            }
            formatCompanyName();
        }
        loadingUserInfo.value = false;
    }

    const setMainCompany = async (bin: string) => {
        localStorage.setItem('selectedCompanyBin', bin);
        selectedCompanyBin.value = bin;
    }

    const getUserData = async () => {
        isAuth.value = true;
        await getUserInfo();
        userProfile.value = await api.getUserProfile();
        if(userProfile.value.lang != locale.value) {
            api.saveUserLocale(locale.value).then()
        }
        userInfo.value = {
            email: userProfile.value.email,
            lastName: userProfile.value.lastname,
            firstName: userProfile.value.firstname,
        }
        setUserData(userProfile.value.firstname + ' ' + userProfile.value.lastname, userProfile.value.email);
    }

    const updateToken = async (token: string, refreshToken: string, expires_in: number) => {
        localStorage.setItem('token', token);
        localStorage.setItem('refresh_token', refreshToken);
        localStorage.setItem('expires_in', String(new Date().getTime() + expires_in * 1000));

        const domain = isProd() ? '.ismet.kz' : 'i-smet.kz';
        setCookie('GATEWAY', token, 1, '/', domain);

        await getUserData()
        // api.reportUserDetails().then();
    }

    const authLogin = async (lang: string, login: string, password: string) => {
        const data = await api.login(lang, login, password);
        await updateToken(data.access_token, data.refresh_token, data.expires_in);
    }
    const checkingCodeSMSAndGettingTokenOtp = async (phoneNumber: string, code: string, lang: string) => {
        const data = await api.checkingCodeSMSAndGettingTokenOtp(phoneNumber, code, lang);
        await updateToken(data.access_token, data.refresh_token, data.expires_in);
    }
    const checkingCodeEmailAndGettingTokenOtp = async (email: string, code: string, lang: string) => {
        const data = await api.checkingCodeEmailAndGettingTokenOtp(email, code, lang);
        await updateToken(data.access_token, data.refresh_token, data.expires_in);
    }
    const checkingCodeSMSAndGettingTokenReg = async (phoneNumber: string, code: string, lang: string) => {
        const data = await api.checkingCodeSMSAndGettingTokenReg(phoneNumber, code, lang);
        await updateToken(data.access_token, data.refresh_token, data.expires_in);
    }
    const checkingCodeEmailAndGettingTokenReg = async (email: string, code: string, lang: string) => {
        const data = await api.checkingCodeEmailAndGettingTokenReg(email, code, lang);
        await updateToken(data.access_token, data.refresh_token, data.expires_in);
    }
    const sendNewPassword = async (target: string, code: string, lang: string, newPassword: string) => {
        const data = await api.sendNewPassword(target, code, lang, newPassword);
        await updateToken(data.access_token, data.refresh_token, data.expires_in);
    }
    const refreshAuthToken = async (refreshToken: string) => {
        const data = await api.refreshAuthToken(refreshToken);
        await updateToken(data.access_token, data.refresh_token, data.expires_in);
    }
    const getRefreshToken = async () => {
        const data = await api.getRefreshToken();
        await updateToken(data.access_token, data.refresh_token, data.expires_in);
    }

    const logout = async () => {
        try {
            isAuth.value = false;
            selectedCompanyBin.value = null;
            contextCompanies.value = [];
            const domain = isProd() ? '.ismet.kz' : '.i-smet.kz';
            const ismetIdNote = localStorage.getItem('ismet-id-note');
            localStorage.clear()
            sessionStorage.clear();
            await api.logOut();
            deleteCookie('GATEWAY', '/', domain);
            userInfo.value = null;
            if (ismetIdNote) {
                localStorage.setItem('ismet-id-note', ismetIdNote);
            }
        } catch (e) {
            console.log(e)
        }
    }

    const isBlockingChangeCompany = computed(() => {
        return route.name == 'DraftDoc' || route.name == 'EditDocument' || route.name == 'CopyDocument';
    });

    const {data: tariffData} = useQuery({
        queryKey: ['activeTariff', {selectedContextCompany, isAuth}],
        queryFn: async () => {
            if (!selectedContextCompany.value || !isAuth.value) return null;
            try {
                const data = await OrderApi.getActiveOrder(selectedContextCompany.value.bin);
                userTariffShortInfo.value.active = true;
                if (data.tariff.maxNumOfDocs != 0) {
                    userTariffShortInfo.value.type = 'by_count';
                } else {
                    userTariffShortInfo.value.type = 'by_date';
                }

                if (data.tariff.maxNumOfDocs != 0) {
                    userTariffShortInfo.value.active = data.tariff.maxNumOfDocs - data.docCount > 0;
                } else {
                    let endDateStr = "";
                    if (data.tariff.endDate) {
                        endDateStr = data.tariff.endDate;
                    } else if (data.endDate) {
                        endDateStr = data.endDate;
                    } else {
                        userTariffShortInfo.value.active = false;
                    }
                    const [date, time] = endDateStr.split(' ');
                    const [day, month, year] = date.split('.');
                    const [hour, minute, second] = time.split(':');
                    const endDate = new Date(+year, +month - 1, +day, +hour, +minute, +second);
                    const endDateUnix = endDate.getTime();
                    const curDate = new Date();

                    userTariffShortInfo.value.active = endDateUnix > curDate.getTime();
                }

                return data;
            } catch (e: AxiosError | any) {
                userTariffShortInfo.value.active = false;
                if (e.response.data.error?.messageEn === 'Free tariff already used') {
                    userTariffShortInfo.value.type = 'by_count';
                } else {
                    userTariffShortInfo.value.type = 'by_date';
                }
                return null;
            }
        },
        refetchInterval: 30000,
    });

    onMounted(() => {
        platform.value = getMobileOperatingSystem();
    });

    const changeUserLang = (lang: string) => {
        if(isAuth.value) {
            api.saveUserLocale(lang).then()
        } else {
            const domain = isProd() ? '.ismet.kz' : '.i-smet.kz';
            //400 (9600) дней (так же приходит с бэка)
            setCookie('lang', lang, 9600, '/', domain)
        }
    }

    const isMobile = computed(() => platform.value ? ['android', 'ios'].includes(platform.value) : false);

    return {
        userInfo,
        getUserInfo,
        isAuth,
        isSelectCompanyBlocked,
        isLoader,
        authLogin,
        checkingCodeSMSAndGettingTokenOtp,
        checkingCodeEmailAndGettingTokenOtp,
        checkingCodeSMSAndGettingTokenReg,
        checkingCodeEmailAndGettingTokenReg,
        sendNewPassword,
        refreshAuthToken,
        getUserData,
        userProfile,
        logout,
        selectedContextCompany,
        setMainCompany,
        contextCompanies,
        userTariffShortInfo,
        isBlockingChangeCompany,
        tariffData,
        isMobile,
        platform,
        getRefreshToken,
        changeUserLang,
        loadingUserInfo,
    }
});
